import type { DynamicFieldDefinition } from './formDefinition'

// A field that depends on another answer only appears once that answer is one
// of the listed values.
export interface VisibilityCondition {
	key: string
	in: string[]
}

// answerValue reduces an answer to the option a condition names. Yes/no answers
// are stored in several shapes on their way through the form: the radio ids
// ("buyer-0-personHasContract-yes"), the stringified booleans of the dynamic
// payload, and the plain options.
export function answerValue(answer: string): string {
	if (answer === 'yes' || answer === 'true' || answer.endsWith('-yes')) return 'yes'
	if (answer === 'no' || answer === 'false' || answer.endsWith('-no')) return 'no'
	return answer
}

// FieldRules is what the schema says about each field: whether the form asks
// it, whether it is mandatory, when it appears, and whether it is asked of the
// first buyer only. The sections take all of it from here rather than deciding
// for themselves.
//
// The back-end reads the same schema in schema.go, and the cases in
// api/form/v1/testdata/visibility_cases.json hold both to the same answers.
export class FieldRules {
	private readonly asked: Set<string>
	private readonly required: Set<string>
	private readonly conditions: Map<string, VisibilityCondition>
	private readonly firstBuyerOnly: Set<string>

	constructor(fields: Pick<DynamicFieldDefinition, 'key' | 'required' | 'visibleWhen' | 'firstBuyerOnly'>[] = []) {
		this.asked = new Set(fields.map(field => field.key))
		this.required = new Set(fields.filter(field => field.required).map(field => field.key))
		this.conditions = new Map(
			fields.flatMap(field => (field.visibleWhen ? [[field.key, field.visibleWhen] as const] : [])),
		)
		this.firstBuyerOnly = new Set(fields.filter(field => field.firstBuyerOnly).map(field => field.key))
	}

	// Whether the form asks any of these questions, whatever the answers.
	asks(...keys: string[]): boolean {
		return keys.some(key => this.asked.has(key))
	}

	// Whether any of these questions is mandatory once shown.
	requires(...keys: string[]): boolean {
		return keys.some(key => this.required.has(key))
	}

	isFirstBuyerOnly(key: string): boolean {
		return this.firstBuyerOnly.has(key)
	}

	// Whether the question appears, given the answers so far, keyed by the
	// schema's field keys.
	//
	// A condition only holds when the answer it depends on is itself shown: once
	// a buyer is no longer married, a contract they had said yes to no longer
	// brings the contract questions up. A condition on a question the form does
	// not ask holds, so dropping that question does not hide what depended on
	// it.
	shows(key: string, answers: Record<string, string>): boolean {
		return this.showsWithin(key, answers, this.conditions.size)
	}

	// Follows the chain of conditions at most depth steps, so a schema whose
	// conditions loop hides the fields in the loop instead of recursing forever.
	private showsWithin(key: string, answers: Record<string, string>, depth: number): boolean {
		if (!this.asked.has(key)) return false
		const condition = this.conditions.get(key)
		if (!condition || !this.asked.has(condition.key)) return true
		if (depth === 0 || !this.showsWithin(condition.key, answers, depth - 1)) return false
		return condition.in.includes(answerValue(answers[condition.key] ?? ''))
	}
}
